import React, { FC, useCallback, useMemo, useState } from "react";

import { useAuthContext } from "../../../auth";
import { useDataLayer } from "../../../hooks/use-data-layer";
import { GoogleAnalyticsEvent } from "../../../utils/analytics";
import { Button } from "../../buttons/button";
import { Cta } from "../../buttons/cta";
import { Modal } from "../../modal";
import { useEventDetail } from "../event-detail/event-detail-context";
import { EventsLabels, EventSubscriptionMethod, EventType } from "../types";
import { useEventDates } from "../use-event-dates";

const subscribeCta: Record<EventType, keyof EventsLabels> = {
  [EventType.IN_PERSON]: "event-subscribe-cta-in-presence",
  [EventType.DIGITAL]: "event-subscribe-cta-online",
  [EventType.BOTH]: "event-subscribe-cta-both",
};

export const EventHeroSubscribe: FC = () => {
  const { event, labels, subscription } = useEventDetail();

  // This component is not rendered if event is null or undefined, we can safely use the non-null assertion
  const { isSubscriptionOpen } = useEventDates(event!);
  const { logged, login } = useAuthContext();
  const [open, setOpen] = useState(false);

  const isSubscriptionUpgradable = useMemo(() => {
    const isSubscribedOnline =
      subscription?.subscriptionMethod === EventSubscriptionMethod.DIGITAL_SUBSCRIPTION;
    const inPersonMethods = (event?.subscriptionTypes ?? [])
      .filter((sub) => sub.subscriptionMethod === EventSubscriptionMethod.IN_PERSON_SUBSCRIPTION)
      .filter((sub) => !sub.soldOut);
    return isSubscriptionOpen && isSubscribedOnline && inPersonMethods.length > 0;
  }, [event?.subscriptionTypes, isSubscriptionOpen, subscription?.subscriptionMethod]);

  const subscriptionCtaLabel = useMemo(() => {
    if (subscription) {
      if (subscription.subscriptionMethod === EventSubscriptionMethod.IN_PERSON_SUBSCRIPTION) {
        return labels["event-participate-cta-both"];
      }
      if (isSubscriptionUpgradable) {
        return labels["event-subscribe-cta-in-presence"];
      }
      return labels["event-participate-cta-online"];
    }

    return isSubscriptionOpen ? labels[subscribeCta[event!.eventType]] : null;
  }, [event, isSubscriptionOpen, isSubscriptionUpgradable, labels, subscription]);

  const allowedSubscriptionTypes = useMemo(() => {
    const allTypes = event?.subscriptionTypes ?? [];
    if (isSubscriptionUpgradable) {
      return allTypes.filter(
        (sub) => sub.subscriptionMethod === EventSubscriptionMethod.IN_PERSON_SUBSCRIPTION
      );
    }
    return allTypes;
  }, [event?.subscriptionTypes, isSubscriptionUpgradable]);

  const getSubscriptionUrlWithSearchParam = useCallback((url: string) => {
    if (window) {
      const typedUrl = new URL(url);
      typedUrl.searchParams.append("fromurl", window.location.href);
      return typedUrl.toString();
    }
    return url;
  }, []);

  const { pushEvent } = useDataLayer();
  const onSubscribe = useCallback(() => {
    pushEvent({
      event: GoogleAnalyticsEvent.EventAccreditationCta,
      event_name: event?.title ?? null,
    });
  }, [event, pushEvent]);

  return (
    <div>
      {subscriptionCtaLabel && (
        <div className="text-xl font-light mb-4 text-center lg:text-left">
          {subscriptionCtaLabel}
        </div>
      )}

      <div className="flex flex-col gap-4">
        {(!subscription || isSubscriptionUpgradable) && isSubscriptionOpen && (
          <div className="flex flex-wrap gap-4 items-center">
            <Button variant="light" onClick={() => setOpen(true)}>
              {labels["event-subscribe-button"]}
            </Button>
            {!logged && (
              <div className="flex flex-wrap gap-2 text-sm">
                {labels["event-already-subscribed-question"]}
                <button type="button" className="font-bold underline" onClick={() => login?.()}>
                  {labels["event-sign-in"]}
                </button>
              </div>
            )}
          </div>
        )}
        {subscription && isSubscriptionUpgradable && (
          <div className="text-xl font-light mt-4 text-center lg:text-left">
            {labels["event-participate-cta-online"]}
          </div>
        )}
        {subscription && (
          <Cta
            variant={!subscription || isSubscriptionUpgradable ? "light-outline" : "light"}
            pageLink={null}
            externalUrl={subscription.urlSubscription}
            label={labels["event-go-to-agenda"] ?? ""}
          />
        )}
      </div>

      <Modal isOpen={open} onClose={() => setOpen(false)}>
        <div className="p-2 md:p-4">
          <h3 className="text-primary font-medium text-2xl md:text-3xl mb-6">
            {labels["event-subscribe-modal-title"]}
          </h3>
          {allowedSubscriptionTypes.map(
            ({ title, subscribeUrl: _subscribeUrl, subscriptionMethod, soldOut }, index) => {
              const subscribeUrl = getSubscriptionUrlWithSearchParam(_subscribeUrl);
              const isInPerson =
                subscriptionMethod === EventSubscriptionMethod.IN_PERSON_SUBSCRIPTION;
              return (
                <div key={index} className="mt-8">
                  <h4 className="font-medium text-xl mb-4">{title}</h4>
                  <Cta
                    onClick={onSubscribe}
                    externalUrl={subscribeUrl}
                    pageLink={null}
                    disabled={soldOut}
                    label={
                      isInPerson
                        ? labels["event-subscribe-in-presence-button"] ?? ""
                        : labels["event-subscribe-online-button"] ?? ""
                    }
                    size="sm"
                    variant={isInPerson ? "primary" : "primary-outline"}
                  />
                </div>
              );
            }
          )}
        </div>
      </Modal>
    </div>
  );
};
